// Importing React and necessary libraries
import React from "react";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles"; // For Material-UI theming
import { useHistory } from "react-router-dom"; // For routing
import { connect } from "react-redux"; // For connecting to Redux store
import PropTypes from "prop-types"; // For type-checking props
import "./modules/payout/assets/style/index.scss"; // Custom styles for payout module

// Importing core components and styles
// eslint-disable-next-line import/no-unresolved
import "./assets/css/material-dashboard-react.css?v=1.9.0"; // Custom CSS for Material Dashboard React
import Admin from "./layouts/Admin"; // Admin layout component

// Defining the default theme for Material-UI
let theme = createTheme({
  palette: {
    secondary: {
      main: "#003c6e", // Secondary main color
      light: "#6aaac8", // Light shade for graphs
      contrastText: "#29586e", // Contrast text for graphs
      dark: "#8ec8d1", // Dark shade for graphs
    },
    tableColor: {
      header: "#003c6e", // Table header color
      borderTop: "#0d1619", // Table border top color
      paggination: "#3e5715", // Pagination color
      clickable: "#481557", // Clickable text color
      cellDiscription: "#6aaac8", // Cell description color
      cellFontColor: "#000000", // Cell font color
      tableHeaderTextColor: "#ffffff", // Table header text color
    },
    offerDetails: {
      offerDetailsHeaderCardColor1: "#81ca90", // Offer details header card color 1
      offerDetailsHeaderCardColor2: "#6ba376", // Offer details header card color 2
      activeTab: "#fafafa", // Active tab color
      activeTabBorderColor: "#bfbfbf", // Active tab border color
      inActiveBorderColor: "#bfbfbf", // Inactive tab border color
      inActiveTab: "#dcdcdc", // Inactive tab color
    },
    primary: {
      main: "#003c6e", // Primary main color
      light: "#bfbdbd", // Light shade
      dark: "#696969", // Dark gray color
      contrastText: "#fff", // White contrast text
    },
    error: {
      main: "#ff4747",
      light: "#",
      dark: "#",
      contrastText: "#",
    },
    warning: {
      main: "#",
      light: "#",
      dark: "#",
      contrastText: "#",
    },
    info: {
      main: "#4da189",
      light: "#f5b058",
      dark: "#354dd4",
      contrastText: "#b8fcf9", // icon Box color
    },
    success: {
      main: "#228B22",
      light: "#",
      dark: "#",
      contrastText: "#144b8e",
    },
  },
  overrides: {
    MuiTypography: {
      body2: {
        fontSize: [16, "!important"], // Typography override for body2
      },
    },
  },
});

// AppView component
const AppView = (props) => {
  const history = useHistory();
  // React state hooks for theme management
  const [themeChange, setThemeChange] = React.useState(props.app.appTheme);
  const [defaultTheme, setDefaultTheme] = React.useState(
    props.app.defaultTheme
  );

  // Effect to handle theme changes
  React.useEffect(() => {
    if (
      themeChange !== props.app.appTheme
      && props.app.appTheme
      && localStorage.getItem("token")
    ) {
      const themeObj = props.app.appTheme[0];
      delete themeObj.Theme_Name;
      delete themeObj._id;
      themeObj.error.main = "#ff4747";
      theme = createTheme({
        palette: themeObj,
        overrides: {
          MuiTypography: {
            subtitle1: {
              fontSize: [14, "!important"],
              fontStyle: "italic",
              textTransform: "none",
              color: "#a53789",
            },
            body1: {
              fontSize: [14, "!important"],
              lineHeight: 1.2,
            },
            body2: {
              fontSize: [14, "!important"],
            },
            h1: {
              fontWeight: "bold",
              textTransform: "none",
              fontSize: [15, "!important"],
            },
          },
          MuiAutocomplete: {
            input: {
              fontSize: "14px",
              color: "#696969",
            },
          },

          MuiButton: {
            contained: {
              backgroundColor: themeObj.primary.main,
              color: themeObj.primary.contrastText,
              "&:hover": {
                backgroundColor: themeObj.primary.light,
                color: "#3c485",
              },
              textTransform: "capitalize",
              borderRadius: "3px",
              height: "32px",
            },
            outlined: { textTransform: "capitalize" },
          },
          MuiTableSortLabel: {
            root: {
              color: "white",
              "&:hover": {
                color: "white",
              },
            },

            active: {
              color: "white !important",
              "&:hover": {
                color: "white",
              },
            },
            iconDirectionDesc: {
              color: "white !important",
              "&:hover": {
                color: "white",
              },
            },
            iconDirectionAsc: {
              color: "white !important",
              "&:hover": {
                color: "white",
              },
            },
          },
        },
      });
      setThemeChange(props.app.appTheme);
    }
    if (
      defaultTheme !== props.app.defaultTheme &&
      props.app.defaultTheme.data
    ) {
      const themeObj = props.app.defaultTheme.data[0];
      delete themeObj.Theme_Name;
      const logoImage = themeObj.Logo_Image;
      delete themeObj.Logo_Image;
      delete themeObj._id;
      themeObj.error.main = "#ff4747";
      theme = createTheme({
        palette: themeObj,
        overrides: {
          MuiTypography: {
            body1: {
              fontSize: [14, "!important"],
              lineHeight: 1.2,
            },
            subtitle1: {
              fontStyle: "italic",
              textTransform: "none",
              fontSize: [14, "!important"],
              color: "#a53789",
            },
            body2: {
              fontSize: [14, "!important"],
            },
            h1: {
              fontWeight: "bold",
              textTransform: "none",
              fontSize: [15, "!important"],
              color: themeObj.primary.dark,
            },
          },
          MuiButton: {
            contained: {
              backgroundColor: themeObj.primary.main,
              height: "unset",
              color: themeObj.primary.contrastText,
              "&:hover": {
                backgroundColor: themeObj.primary.light,
                color: "#3c485",
              },
              textTransform: "capitalize",
              borderRadius: "3px",
            },
            outlined: { textTransform: "capitalize" },
          },
          MuiTableSortLabel: {
            root: {
              color: "white !important",
              "&:hover": {
                color: "white !important",
              },
            },
            active: {
              color: "white !important",
              "&:hover": {
                color: "white !important",
              },
            },
          },
        },
      });
      themeObj.Logo_Image = logoImage;
      setDefaultTheme(props.app.defaultTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  // Rendering the component
  return (
    <MuiThemeProvider theme={theme}>
      <Admin history={history} />
    </MuiThemeProvider>
  );
};

// Prop types for AppView
AppView.propTypes = {
  app: PropTypes.object,
};

// Mapping Redux state to props
const mapStateToProps = (app) => ({
  app: app.app,
});

// Connecting AppView to Redux store
export default connect(mapStateToProps, null)(AppView);
